import Offcanvas from "react-bootstrap/Offcanvas";
import "bootstrap/dist/css/bootstrap.min.css";
import { RxCross2 } from "react-icons/rx";
import React, { useEffect } from "react";
import styles from "./Dashboard.module.css";
import { AttachMoney, CreditCard } from "@mui/icons-material";
import { Box, Grid, Typography, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  viewCredit,
  fetchMyCredit,
  fetchManagerConfigurations,
  createCreditOrder,
  addMyCredit,
} from "../../redux/credit/creditSlice"; // Import Redux actions
import { useState } from "react";
import { customTextColor, formatDate } from "../../utils/const";
import { getProfile } from "../../redux/profile/profileSlice";
import { toast } from "react-toastify";
import Sidebar from "./CreditHistory";

// Razorpay script loader
const loadRazorpayScript = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const fieldStyle = {
  width: "100%",
  "& .MuiInputBase-root": {
    paddingLeft: "37px", // Adjust padding to make space for the icon
  },
  "& .MuiInputLabel-root": {
    left: "37px", // Adjust label position to align with input text
  },
  "& .MuiInputBase-input": {
    paddingBottom: "10px", // Add padding to the input text itself to make space for the icon
  },
  "& .MuiInputLabel-shrink": {
    left: "37px", // Adjust label position when it shrinks
  },
};

function AddCreditDrawer({ handleClose, show, handleShowTransfor }) {
  const dispatch = useDispatch();
  const myCredit = useSelector((state) => state.credit.creditDetails?.data);
  const profile = useSelector((state) => state?.profile?.data?.profile);
  const creditStatus = useSelector((state) => state.credit?.status);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const managerConfigurations = useSelector(
    (state) => state.credit.managerConfigurations
  );

  const creditValue =
    managerConfigurations?.filter(
      (data) => data.name === "no_of_credit_per_dollar"
    )?.[0]?.value || 0;

  const [amount, setAmount] = useState(0);
  const [credit, setCredit] = useState(0);
  const [disableAmount, setDisableAmount] = useState(false);
  const [disableCredit, setDisableCredit] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    dispatch(fetchMyCredit());
    dispatch(getProfile());
    dispatch(fetchManagerConfigurations());
    dispatch(viewCredit());
  }, [dispatch]);

  useEffect(() => {
    if (creditStatus === "add_credit_succeeded") {
      dispatch(fetchMyCredit());
      setAmount("");
      setCredit("");
      setDisableAmount(false);
      setDisableCredit(false);
    }
  }, [creditStatus]);

  const handlePayment = async () => {
    // Start loading

    // Validate the amount
    if (!amount || Number(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    // Load Razorpay script
    const loaded = await loadRazorpayScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!loaded) {
      alert(
        "Failed to load Razorpay script. Please check your internet connection."
      );
      return;
    }

    const options = {
      key: "rzp_test_epr1KvG8SLtVdK", // Replace with your Razorpay Key ID
      // key: "rzp_live_aSXrBIIQBTOjX2", // Replace with your Razorpay Key ID
      amount: Number(amount) * 100, // Amount in paise (e.g., ₹2000 = 2000 * 100)
      currency: "INR",
      name: "Povital Pvt Ltd",
      description: `Credit payment of ${credit} for User ID: ${profile?.user_id}`,
      handler: (response) => {
        console.warn(response);
        toast.success(
          `Payment successful! Payment ID: ${response.razorpay_payment_id}`
        );

        const paymentData = {
          amount: Number(amount),
          currency: "INR",
          user_name: profile?.username,
          contact: profile?.mobile_no,
          status: response.razorpay_payment_id ? "successful" : "failed",
        };

        // Dispatching the payment data to the Redux store
        dispatch(createCreditOrder(paymentData)).then(() => {
          dispatch(
            addMyCredit({
              credit_type: "self_added",
              amount: credit || 0,
            })
          );
          // dispatch(fetchMyCredit());
          // setAmount("");
          // setCredit("");
        });
      },
      prefill: {
        name: profile?.full_name || "John Doe", // Use dynamic user data
        email: profile?.email || "johndoe@example.com",
        contact: profile?.mobile_no || "9999999999",
      },
      theme: {
        color: "#3399cc",
      },
    };

    // Create the Razorpay object and open the payment modal
    const paymentObject = new window.Razorpay(options);
    paymentObject.open();

    // Stop loading once the Razorpay modal is opened or if there's an error
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;
    // Allow only numbers and limit the input to 3 digits
    if (/^\d{0,3}(\.\d{0,3})?$/.test(value)) {
      let numberValue = parseFloat(value);
      // Ensure values greater than 0 but less than 1 round up to 1
      if (numberValue > 0 && numberValue < 1) {
        numberValue = 1;
      }
      // Prevent negative values
      if (numberValue < 0) {
        setError("Amount cannot be negative");
        numberValue = 0;
      } else {
        setError("");
      }
      setAmount(numberValue);
      setCredit(numberValue ? (numberValue * creditValue).toFixed(0) : "0"); // Calculate credit
      setDisableCredit(!!numberValue);
      setDisableAmount(false);
    }
  };

  const handleCreditChange = (e) => {
    let value = e.target.value;
    // Allow only numbers and limit the input to 5 digits
    if (/^\d{0,5}$/.test(value)) {
      let numberValue = parseFloat(value);
      // Prevent negative values
      if (numberValue < 0) {
        setError("Credit cannot be negative");
        numberValue = 0;
      } else {
        setError("");
      }
      setCredit(numberValue);
      // Calculate amount; enforce minimum of 1 dollar if credit > 0 but amount is < 1
      let calculatedAmount = numberValue ? numberValue / creditValue : 0;
      if (numberValue > 0 && calculatedAmount < 1) {
        calculatedAmount = 1;
      }
      setAmount(calculatedAmount.toFixed(0));
      setDisableAmount(!!numberValue);
      setDisableCredit(false);
    }
  };

  const handleClickTransferCredit = () => {
    handleClose();
    handleShowTransfor();
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      scroll={false}
      backdrop={false}
      style={{
        border: "none",
        marginTop: "74px",
        width: "35%",
        // padding: "0 0 0 10px",
        boxShadow: "-4px 0 10px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Offcanvas.Header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <button
          className={styles.pay_credit_btn}
          onClick={handleClickTransferCredit}
          style={{ backgroundColor: "#4287f5", marginTop: "0" }}
        >
          Transfer Credit
        </button>
        <RxCross2
          style={{
            fontSize: "22px",
            cursor: "pointer",
            color: customTextColor,
          }}
          onClick={handleClose}
        />
      </Offcanvas.Header>
      <Offcanvas.Body style={{ padding: "0" }}>
        <Box>
          <Box className={styles.creditViewSection}>
            <Typography>
              Available Credit{" "}
              <a style={{ fontSize: "20px" }}>
                <b>{myCredit?.total_credits}</b>
              </a>
            </Typography>
            <Typography>
              Received {myCredit?.earned_credits} Bonus Credit
            </Typography>
            <Typography
              sx={{
                marginBottom: "0 !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>
                {" "}
                No transaction since{" "}
                {myCredit?.updated_at !== undefined &&
                  formatDate(myCredit?.updated_at)}
              </span>
              <span
                style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => setIsDrawerOpen(true)}
              >
                View Credit History
              </span>
            </Typography>
          </Box>

          <Box sx={{ backgroundColor: "#fff", margin: "20px" }}>
            <Typography>
              <b>Add Your Credit to Account</b>
            </Typography>
            <Box sx={{ textAlign: "center" }}>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={6}
                  lg={6}
                  sx={{
                    padding: "10px 20px 10px 0",
                    position: "relative",
                  }}
                >
                  <Box
                    className={styles.icon_style_credit}
                    sx={error ? { top: "43%" } : {}}
                  >
                    <AttachMoney style={{ fontSize: "30px" }} />
                  </Box>
                  <TextField
                    id="standard-basic"
                    label="Amount"
                    variant="standard"
                    type="number"
                    sx={fieldStyle}
                    value={Number.isNaN(amount) ? "" : amount}
                    onChange={handleAmountChange}
                    disabled={disableAmount}
                    error={!!error}
                    // helperText={error}
                  />
                  {error ? <div className={styles.error}>{error}</div> : null}
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={6}
                  lg={6}
                  sx={{
                    padding: "10px 0 10px 0",
                    position: "relative",
                  }}
                >
                  <Box
                    className={styles.icon_style_credit}
                    sx={error ? { top: "43%" } : {}}
                  >
                    <CreditCard style={{ fontSize: "30px" }} />
                  </Box>
                  <TextField
                    label="Credit"
                    variant="standard"
                    sx={fieldStyle}
                    value={Number.isNaN(credit) ? "" : credit}
                    onChange={handleCreditChange}
                    disabled={disableCredit}
                  />
                </Grid>
              </Grid>

              <button className={styles.pay_credit_btn} onClick={handlePayment}>
                {creditStatus === "loading" ? "Loading" : `Pay ${amount}`}
              </button>
            </Box>
          </Box>
        </Box>
      </Offcanvas.Body>
      <Sidebar
        isOpen={isDrawerOpen}
        handleClose={() => setIsDrawerOpen(false)}
        creditHistory={myCredit?.credit_history || []}
      />
    </Offcanvas>
  );
}

export default AddCreditDrawer;
